import type { InfiniteData } from '@tanstack/react-query';
import { AuthType, init } from '@thoughtspot/visual-embed-sdk';
import type { ManagerGetCampaignModel } from 'entities/campaign';
import { ReportsRoutes } from 'entities/reports';
import type { TSTokenModel } from 'entities/reports';
import { useGetContractorServicesForFilters } from 'entities/service';
import { useMemo } from 'react';
import { api, type CollectionSuccessApiResponse } from 'shared/lib/api';
import { useLocation } from 'react-router-dom';
import { useUrlResolver } from 'shared/lib/url';

// Initialize ThoughtSpot SDK
export const initTS = () =>
  init({
    thoughtSpotHost: 'https://homebuddy.thoughtspot.cloud',
    authType: AuthType.TrustedAuthTokenCookieless,
    getAuthToken: async () => {
      const response = await api.post<TSTokenModel>(ReportsRoutes.app.getTSToken, undefined);

      return response.data.token;
    },
    autoLogin: true,
    customizations: {
      style: {
        customCSS: {
          variables: { '--ts-var-root-background': 'transparent' },
          rules_UNSTABLE: {
            '.pinboard-header-module__pinboardStickyHeaderContainer': {
              'box-shadow': 'unset',
            },
            '.pinboard-header-module__pinboardHeaderContainer': {
              'border-bottom': 'unset',
            },
          },
        },
      },
    },
  });

export const useGetQueryParams = (queryKey: string) => {
  const location = useLocation();
  const { getAllParams } = useUrlResolver();
  const values = useMemo(() => getAllParams(queryKey), [location]);

  return values;
};

export function useGetServicesOptionsAliasId() {
  const services = useGetContractorServicesForFilters().data?.data;

  return services?.map((item) => ({ id: item.alias, text: item.name })) ?? [];
}

export function getCampaignsOptionsNameIdFromInfinite(
  data?: InfiniteData<CollectionSuccessApiResponse<Pick<ManagerGetCampaignModel, 'id' | 'name'>>>
) {
  return data?.pages.flatMap(({ data }) => data.map((item) => ({ id: item.name, text: item.name }))) ?? [];
}

export function getLastSunday() {
  const currentDate = new Date();
  const currentDay = currentDate.getDay();
  const offsetToLastSunday = (currentDay + 7) % 7;

  const lastSundayDate = new Date(currentDate);

  lastSundayDate.setDate(currentDate.getDate() - offsetToLastSunday);

  return lastSundayDate;
}

export function convertDate(date: Date) {
  return (new Date(date).getTime() / 1000).toString().split('.')[0];
}

export function getStartOfADay(date: Date) {
  date.setUTCHours(0, 0, 0, 0);

  return date;
}

export function getLastNWeeks(n: number) {
  const date = new Date();

  date.setDate(date.getDate() - n * 7);

  return convertDate(getStartOfADay(date));
}

export function getLastNMonths(n: number) {
  const date = new Date();

  date.setMonth((date.getMonth() - n) % 12);
  const yearsBack = Math.floor((date.getMonth() - n) / 12);

  if (yearsBack > 0) {
    const prevYear = date.getFullYear() - yearsBack;

    date.setFullYear(prevYear);
  }

  return convertDate(getStartOfADay(date));
}

export function getJanFirstThisYear() {
  const date = new Date();
  const currYear = date.getFullYear();

  const newDate = new Date(currYear, 0, 1);

  return convertDate(newDate);
}

export function getJanFirstLastYear() {
  const date = new Date();
  const prevYear = date.getFullYear() - 1;

  const newDate = new Date(prevYear, 0, 1);

  return convertDate(newDate);
}

export function datepickerMinDate() {
  return new Date('01-01-2024');
}

export function datepickerMaxDate() {
  const date = new Date();

  date.setDate(date.getDate() - 14);

  return getStartOfADay(date);
}
